import { useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { describeError } from '../lib/describeError'
import { errorCode, getPiece, getVersionSource, makeVersions, type DeskResponse } from './api'
import { withRunStarted } from './desk'
import { deskHref, pieceHref } from './links'
import { destinationLabel, type Destination } from './labels'
import { btnPrimary, errorText, field, focus } from './piece/ui'
import { useEditorialTitle } from './titleContext'
import { supportsMembership } from '../lib/brief'
import { VERSION_FORMATS, destinationForKind, initialVersionForm, isLonger, versionBlock, versionsBody, type VersionForm, type VersionFormat } from './versions'

const step = 'flex flex-col gap-3.5 rounded-xl border border-ink-500 bg-ink-700 p-5'
const NOTE_MAX = 500
const OFF = 'Make a version is off for this title. An admin can switch it on in Settings.'
const BUSY = 'A piece is being written for this title. Try again when it finishes.'

function Step({ n, title, note, children }: { n: number; title: string; note?: string; children: ReactNode }) {
  return (
    <section className={step}>
      <h2 className="m-0 flex items-center gap-2.5 font-ui text-[17px] font-semibold text-paper">
        <span className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full bg-paper font-ui text-[13px] font-bold text-ink-900" aria-hidden="true">{n}</span>
        {title}
        {note && <span className="text-[13px] font-normal text-paper-dim">{note}</span>}
      </h2>
      {children}
    </section>
  )
}

const card = (on: boolean, disabled = false) =>
  `relative flex flex-col gap-1.5 rounded-[10px] border p-4 ${disabled ? 'cursor-not-allowed border-ink-500 bg-ink-800 opacity-50' : on ? 'cursor-pointer border-amber-glow bg-amber-wash ring-1 ring-amber-glow' : 'cursor-pointer border-ink-400 bg-ink-800'}`

function VersionForm({ src, sourceTitle, sourceFormat, sourceDestination }: {
  src: { draft_id?: number; article_id?: number }; sourceTitle: string; sourceFormat: VersionFormat | null; sourceDestination: Destination
}) {
  const { section, publicationSlug: slug } = useEditorialTitle()
  const nav = useNavigate()
  const qc = useQueryClient()
  const membership = supportsMembership(slug)
  const desk = deskHref(section.path)
  const [f, setF] = useState<VersionForm>(() => initialVersionForm(sourceFormat, sourceDestination, membership))
  const [error, setError] = useState<string | null>(null)

  const start = useMutation({
    mutationFn: () => makeVersions(slug, versionsBody(f, src)),
    onSuccess: (res) => {
      if (res.draft_ids && res.draft_ids.length > 0) {
        qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
        nav(pieceHref(section.path, res.draft_ids[0]))
        return
      }
      if (res.started) {
        qc.setQueryData<DeskResponse>(['editorial-desk', slug], (d) => withRunStarted(d, res.status))
        qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
        qc.invalidateQueries({ queryKey: ['section-draft-status', slug] })
        nav(desk)
        return
      }
      setError(res.already_running ? BUSY : 'Nothing was started. Try again.')
      qc.invalidateQueries({ queryKey: ['section-draft-status', slug] })
    },
    onError: (e) => {
      const code = errorCode(e)
      setError(e instanceof ApiError && (code === 'versions_off' || code === 'switched_off') ? OFF : describeError(e))
    },
  })

  const block = versionBlock(f)
  const setChoice = (format: VersionFormat, patch: Partial<VersionForm['choices'][number]>) =>
    setF((p) => ({ ...p, choices: p.choices.map((c) => (c.format === format ? { ...c, ...patch } : c)) }))
  const destOptions: { key: Destination; label: string }[] = [
    { key: 'section', label: 'Free section' },
    { key: 'membership', label: 'Members only' },
    { key: 'both', label: destinationLabel('both', section.label) },
  ]

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Breadcrumb" className="font-ui text-[13px] text-paper-dim"><Link to={desk} className="text-amber-glow underline underline-offset-2">Desk</Link> / Make a version</nav>
      <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[40px]">Make a version</h1>
      <p className="m-0 font-ui text-[14px] text-paper-dim">Of “{sourceTitle}”. The source stays as it is; each version is a new piece you review.</p>
      <form className="flex max-w-[760px] flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); if (!block && !start.isPending) { setError(null); start.mutate() } }}>
        <Step n={1} title="Which lengths?">
          <div className="flex flex-col gap-2.5">
            {VERSION_FORMATS.map((vf) => {
              const c = f.choices.find((x) => x.format === vf.format)!
              const isSource = vf.format === sourceFormat
              return (
                <div key={vf.format} className="flex flex-col gap-2">
                  <label className={card(c.on, isSource)}>
                    <input type="checkbox" checked={c.on} disabled={isSource} onChange={(e) => setChoice(vf.format, { on: e.target.checked })}
                           className={`absolute right-4 top-4 m-0 h-[18px] w-[18px] accent-amber-glow ${focus}`} />
                    <strong className="pr-6 font-ui text-[16px] font-semibold text-paper">{vf.label}</strong>
                    <span className="font-ui text-[13px] text-paper-dim">{vf.words}</span>
                    {isSource && <span className="font-ui text-[12px] text-paper-faint">This is the source's length</span>}
                  </label>
                  {c.on && !isSource && (
                    <div className="flex flex-col gap-2 pl-2">
                      {membership && (
                        <div role="radiogroup" aria-label={`Who will read the ${vf.label} version`} className="flex flex-wrap gap-0.5 self-start rounded-[9px] bg-ink-600 p-[3px]">
                          {destOptions.map((o) => (
                            <button key={o.key} type="button" role="radio" aria-checked={c.destination === o.key}
                                    onClick={() => setChoice(vf.format, { destination: o.key })}
                                    className={`min-h-[34px] rounded-[7px] px-3 text-[13px] ${focus} ${c.destination === o.key ? 'bg-ink-700 font-bold text-paper shadow-sm' : 'font-semibold text-paper-dim hover:text-paper'}`}>
                              {o.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {isLonger(vf.format, sourceFormat) && (
                        <p className="m-0 font-ui text-[12px] text-paper-dim">Goes longer: the writer researches beyond the source, and every added fact is checked.</p>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Step>

        <Step n={2} title="Start with">
          <fieldset className="m-0 grid gap-2.5 border-0 p-0" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}>
            <legend className="sr-only">Start with</legend>
            {([['ai', 'AI first draft', 'The writer drafts each version from the source.'], ['blank', 'Blank', 'You write it; the source sits alongside.']] as const).map(([k, label, hint]) => (
              <label key={k} className={card(f.start === k)}>
                <input type="radio" name="start" checked={f.start === k} onChange={() => setF((p) => ({ ...p, start: k }))}
                       className="absolute right-4 top-4 m-0 h-[18px] w-[18px] accent-amber-glow" />
                <strong className="pr-6 font-ui text-[16px] font-semibold text-paper">{label}</strong>
                <span className="font-ui text-[13px] text-paper-dim">{hint}</span>
              </label>
            ))}
          </fieldset>
        </Step>

        <Step n={3} title="Note to the writer" note="Optional">
          <label className="flex flex-col gap-1.5">
            <span className="sr-only">Note to the writer</span>
            <textarea className={field} rows={3} maxLength={NOTE_MAX} value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))}
                      placeholder="Optional — e.g. lead with the restoration news" />
          </label>
        </Step>

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" className={btnPrimary} disabled={!!block || start.isPending} title={block ?? undefined}>
            {block ?? (start.isPending ? 'Starting…' : 'Make the version')}
          </button>
          <Link to={desk} className={`font-ui text-[13px] text-paper-dim underline ${focus}`}>Cancel</Link>
        </div>
        {error && <p role="alert" className={errorText}>{error}</p>}
      </form>
    </div>
  )
}

export function MakeVersionPage() {
  const { publicationSlug: slug } = useEditorialTitle()
  const [params] = useSearchParams()
  const location = useLocation()
  const draftId = Number(params.get('draft')) || null
  const articleId = draftId === null ? Number(params.get('article')) || null : null
  const stateTitle = (location.state as { title?: unknown } | null)?.title
  const passedTitle = typeof stateTitle === 'string' && stateTitle ? stateTitle : null
  const src = draftId !== null ? { draft_id: draftId } : articleId !== null ? { article_id: articleId } : null

  // Title, length and kind for any editor of the title (the archive read is admin-only).
  const source = useQuery({ queryKey: ['editorial-version-source', slug, draftId, articleId], queryFn: () => getVersionSource(slug, src!),
                            enabled: src !== null, retry: false })
  const piece = useQuery({ queryKey: ['editorial-piece-source', draftId], queryFn: () => getPiece(draftId as number), enabled: draftId !== null, retry: false })

  if (src === null) {
    return <p role="alert" className="font-ui text-[13px] text-radar-red">Pick a piece to make a version of, from its ••• menu or from Live.</p>
  }
  if (source.isPending || (draftId !== null && piece.isPending)) return <p className="font-ui text-[13px] text-paper-dim">Loading…</p>
  const failed = source.isError ? source.error : draftId !== null && piece.isError ? piece.error : null
  if (failed || !source.data) return <p role="alert" className="font-ui text-[13px] text-radar-red">Could not load that piece. {describeError(failed)}</p>
  const s = source.data
  const d = piece.data
  const draftDest = d ? ((d.destination ?? d.brief?.destination ?? 'section') as Destination) : null
  const title = s.title || passedTitle || (draftId !== null ? `Piece #${draftId}` : `Article #${articleId}`)
  return <VersionForm key={draftId !== null ? `d${draftId}` : `a${articleId}`} src={src} sourceTitle={title}
                      sourceFormat={s.format} sourceDestination={destinationForKind(s.kind, draftDest)} />
}
